import React from "react";
import { FaEye, FaPen, FaTrash } from "react-icons/fa";
import { TH_CLASS, TD_BASE } from "./constants";

export default function UserProfilesTable({
  users,
  page,
  limit,
  isLoading,
  activeFiltersCount,
  onViewProfile,
  onEditUser,
  onDeleteUser,
  deletingUserId,
}) {
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-red-500 border-t-transparent" />
        <p className="mt-4 text-gray-600 font-medium">Loading users...</p>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-semibold text-gray-900">No users found</h3>
        <p className="mt-1 text-sm text-gray-500">
          {activeFiltersCount > 0
            ? "Try adjusting your filters"
            : "No user profiles match your criteria"}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 bg-white min-w-[900px] w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className={`${TH_CLASS}`}>SR. NO</th>
            <th className={TH_CLASS}>First Name</th>
            <th className={TH_CLASS}>Last Name</th>
            <th className={TH_CLASS}>Email</th>
            <th className={TH_CLASS}>Gender</th>
            <th className={TH_CLASS}>Contact No</th>
            <th className={TH_CLASS}>Age</th>
            <th className={TH_CLASS}>Profile %</th>
            <th className={TH_CLASS}>Height</th>
            <th className={TH_CLASS}>GrewUp</th>
            <th className={TH_CLASS}>Occupation</th>
            <th className={TH_CLASS}>Source</th>
            <th className={TH_CLASS}>Created</th>
            <th className={TH_CLASS}>Premium</th>
            <th className={`${TH_CLASS} sticky right-0 bg-gray-50`}>Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user, idx) => (
            <tr key={user.id} className="hover:bg-gray-50 transition-colors">
              <td className={`${TD_BASE} font-medium text-gray-900`}>
                {(page - 1) * limit + idx + 1}
              </td>
              <td className={`${TD_BASE} font-medium text-gray-900`}>{user.firstName}</td>
              <td className={`${TD_BASE} text-gray-800`}>{user.lastName}</td>
              <td className={`px-6 py-4 text-xs md:text-sm text-gray-700 max-w-xs truncate`}>
                {user.email}
              </td>
              <td className={`${TD_BASE} text-gray-700 capitalize`}>{user.gender}</td>
              <td className={`${TD_BASE} text-gray-700`}>{user.contactNo}</td>
              <td className={`${TD_BASE} text-gray-700`}>{user.age}</td>
              <td className={`${TD_BASE} text-gray-700 tabular-nums font-medium`}>
                {user.percentage != null && user.percentage !== ""
                  ? `${Math.round(Number(user.percentage))}%`
                  : "—"}
              </td>
              <td className={`${TD_BASE} text-gray-700`}>{user.height}</td>
              <td className={`${TD_BASE} text-gray-700`}>{user.grewUp}</td>
              <td className={`${TD_BASE} text-gray-700`}>{user.occupation}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${
                    user.is_web
                      ? "bg-blue-100 text-blue-800 border-blue-200"
                      : "bg-green-100 text-green-800 border-green-200"
                  }`}>
                  {user.is_web ? "Web" : "Mobile"}
                </span>
              </td>
              <td className={`${TD_BASE} text-gray-600`}>
                {user.createdDate
                  ? new Date(user.createdDate).toLocaleDateString()
                  : "-"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${
                    user.premium === 1
                      ? "bg-amber-100 text-amber-800 border-amber-200"
                      : "bg-gray-100 text-gray-800 border-gray-200"
                  }`}>
                  {user.premium === 1 ? "Yes" : "No"}
                </span>
              </td>
              <td className={`${TD_BASE} sticky right-0 bg-white hover:bg-gray-50`}>
                <div className="flex items-center gap-1">
                  {onEditUser && (
                    <button
                      type="button"
                      onClick={() => onEditUser(user.id)}
                      disabled={deletingUserId === user.id}
                      className="text-slate-600 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40"
                      title="Edit user">
                      <FaPen className="h-4 w-4" />
                    </button>
                  )}
                  {onDeleteUser && (
                    <button
                      type="button"
                      onClick={() => onDeleteUser(user)}
                      disabled={deletingUserId === user.id}
                      className="text-gray-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40"
                      title="Delete user (Developer only)">
                      {deletingUserId === user.id ? (
                        <span className="inline-block h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <FaTrash className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onViewProfile(user.id)}
                    disabled={deletingUserId === user.id}
                    className="text-red-500 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40"
                    title="View profile">
                    <FaEye className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
